import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getApprovalDomainApprovers,
  createApprovalDomainApprovers,
  updateApprovalDomainApprover,
  deleteApprovalDomainApprover,
} from '@lib/api'
import { approvalDomainApproverKeys } from '@lib/keys'
import { useMemo } from 'react'

export const useApprovalDomainApprovers = (approvalDomainGlobalId = '') => {
  return useQuery(
    approvalDomainApproverKeys.list(approvalDomainGlobalId),
    () => getApprovalDomainApprovers(approvalDomainGlobalId),
    {
      enabled: approvalDomainGlobalId !== '',
    },
  )
}

export const useApprovalDomainApprover = (
  approvalDomainGlobalId: string,
  approverId: number,
) => {
  const { data: approvers = [] } = useApprovalDomainApprovers(
    approvalDomainGlobalId,
  )

  return useMemo(
    () => approvers.find((a) => a.id === approverId),
    [approvers, approverId],
  )
}

interface CreateApprovalDomainApproversParams {
  approvalDomainGlobalId: string
  userIds: number[]
  allFacilities: boolean
  allWorkdayDepartments: boolean
  facilityIds: number[]
  workdayDepartmentIds: string[]
}

export const useCreateApprovalDomainApprovers = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      approvalDomainGlobalId,
      userIds,
      allFacilities,
      allWorkdayDepartments,
      facilityIds,
      workdayDepartmentIds,
    }: CreateApprovalDomainApproversParams) =>
      createApprovalDomainApprovers(
        approvalDomainGlobalId,
        userIds,
        allFacilities,
        allWorkdayDepartments,
        facilityIds,
        workdayDepartmentIds,
      ),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(
          approvalDomainApproverKeys.list(variables.approvalDomainGlobalId),
        )
      },
    },
  )
}

interface UpdateApprovalDomainApproverParams {
  approvalDomainGlobalId: string
  approverId: number
  allFacilities: boolean
  allWorkdayDepartments: boolean
  facilityIds: number[]
  workdayDepartmentIds: string[]
}

export const useUpdateApprovalDomainApprover = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      approvalDomainGlobalId,
      approverId,
      allFacilities,
      allWorkdayDepartments,
      facilityIds,
      workdayDepartmentIds,
    }: UpdateApprovalDomainApproverParams) =>
      updateApprovalDomainApprover(
        approvalDomainGlobalId,
        approverId,
        allFacilities,
        allWorkdayDepartments,
        facilityIds,
        workdayDepartmentIds,
      ),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(
          approvalDomainApproverKeys.list(variables.approvalDomainGlobalId),
        )
      },
    },
  )
}

interface DeleteApprovalDomainApproverParams {
  approvalDomainGlobalId: string
  approverId: number
}

export const useDeleteApprovalDomainApprover = () => {
  const queryClient = useQueryClient()

  return useMutation(
    ({
      approvalDomainGlobalId,
      approverId,
    }: DeleteApprovalDomainApproverParams) =>
      deleteApprovalDomainApprover(approvalDomainGlobalId, approverId),
    {
      onSuccess: (_, variables) => {
        void queryClient.invalidateQueries(
          approvalDomainApproverKeys.list(variables.approvalDomainGlobalId),
        )
      },
    },
  )
}
